/* eslint-disable max-lines */
import {
    MessageActionTypes, MessageProps, MessagesProps,
} from './types'

import React, {
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react'
import {
    removePendingMessage,
    sendMessage,
} from '../../api/action/sendMessage'
import {
    useAppDispatch,
    useAppSelector,
    useVisibilityObserver,
} from '../../hooks'

import {EmbedContext} from 'components/EmbedContext'
import {EmbedEvent} from 'embed/types/events'
import MessagesView from './MessagesView'
import {NinchatHistoryResultsEvent} from '../../api/types/events'
import {NinchatMessageTypes} from '../../api/types/messages'
import {NinchatMessages} from 'api/types'
import {NinchatStoreMessage} from 'api/types/store'

import {config} from 'embed/listeners'
import {confirmationModal} from '../../actions/modal'
import {getHistoryCursorMessageId} from './helpers/history'
import {getWritingMemberUserAttrs} from '../../selectors/ninchat'
import {loadHistory} from '../../api/action/loadHistory'
import {messageAction} from './helpers'
import {updateSession} from '../../api/action/updateSession'
import {useQueues} from 'hooks/ninchat'
import {useSendToParent} from 'embed/utils/event'
import {useTranslation} from 'react-i18next'


const TOP = 'top'
const MIDDLE = 'middle'
const BOTTOM = 'bottom'

const Messages = (props: MessagesProps) => {
    const {t} = useTranslation()

    const {
        chat,
        isAudienceChannel,
        isNarrow,
        messagesContainerRef,
        setHistoryLoadStatus,
        setScrollToBottomView,
        queueId,
    } = props

    const targetOrigin = config.get('targetOrigin') || ''
    const chatId = chat.channel_id ?? chat.user_id
    const isChannel = !!chat.channel_id

    const embed = useContext(EmbedContext)
    const isEmbedQueueChannel = embed?.variant === 'queue' || embed?.variant === 'channel'
    const isEmbedAudience = embed?.variant === 'queue' && isAudienceChannel
    const isEmbedContainer = !embed?.floating

    const queues = useQueues(queueId ? [queueId] : [], false)
    const currentQueue = queueId ? queues[queueId] : undefined

    const chatStatus = useAppSelector(({ninchat}) => (isChannel ? ninchat.user_channels[chatId]?.channel_status : ninchat.user_dialogues[chatId]?.dialogue_status))
    const idle = useAppSelector(({ninchat}) => ninchat.user_attrs.idle)
    const selectedMessages = useAppSelector(({ninchat}) => ninchat._messages[chatId]?.messages)
    const sessionRevision = useAppSelector(({ninchat}) => ninchat._session_revision ?? 0)
    const userId = useAppSelector(({ninchat}) => ninchat.user_id)
    const writingMemberUserAttrs = useAppSelector(({ninchat}) => (isChannel ? getWritingMemberUserAttrs(ninchat, chatId) : undefined))
    const hideJoinAndPartMessages = useAppSelector(({ninchat}) => !!ninchat.user_settings.hide_join_and_part_messages)

    const isChatActive = ['highlight', 'unread'].includes(chatStatus ?? '')

    const topElement = useVisibilityObserver()
    const middleElement = useVisibilityObserver()
    const bottomElement = useVisibilityObserver()

    const [didResize, setDidResize] = useState<boolean>()
    const [forcePosition, setForcePosition] = useState<boolean>(false)
    const [hasMoreHistory, setHasMoreHistory] = useState<boolean>()
    const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false)
    const [messages, setMessages] = useState<NinchatStoreMessage[]>()
    const [scrollToElement, setScrollToElement] = useState<undefined | string>()
    const [writingMessages, setWritingMessages] = useState<NinchatStoreMessage[]>()
    const [receivedMessages, setReceivedMessages] = useState<number>(0)

    const [scrollAtTheBottom, setScrollAtTheBottom] = useState<boolean>(false)

    const isInitialized = useRef<boolean>()
    const previousSessionRevision = useRef(sessionRevision)
    const resizeObserver = useRef<ResizeObserver>()

    const dispatch = useAppDispatch()
    const sendToParent = useSendToParent()

    const historyCursorMessageId = getHistoryCursorMessageId(selectedMessages)


    isInitialized.current = topElement.isActive && bottomElement.isActive

    const isReady = isInitialized.current || hasMoreHistory !== undefined

    const deleteMessageConfirmationModal = (messageId: string) => {
        dispatch(confirmationModal({
            onConfirm: {
                action: 'deleteMessage',
                chat,
                messageId,
            },
            show: true,
            text: t('Are you sure you wish to permanently delete this message?'),
            title: t('Delete message?'),
        }))
    }

    const onDeleteMessage = (messageId: string) => {
        deleteMessageConfirmationModal(messageId)
    }

    const onRemovePendingMessage = (messageId: string) => {
        dispatch(removePendingMessage({
            chatId,
            messageId,
        }))
    }

    const onRetryMessage = (message: NinchatStoreMessage) => {
        if (message._send_params) {
            dispatch(sendMessage({
                ...message._send_params,
                retry_message_id: message.message_id,
            }))
                .unwrap()
                .catch(() => undefined)
        }
    }

    const onAction = (action: MessageActionTypes, message: MessageProps) => {
        messageAction({
            action,
            channelId: chat.channel_id || '',
            dispatch,
            message,
            t,
        })
    }

    useEffect(() => {
        const historyLength = 50

        let prevArea = 0

        resizeObserver.current = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const {
                    devicePixelContentBoxSize, contentRect,
                } = entry

                let height = 0
                let width = 0

                // Use `devicePixelContentBoxSize` if available
                if (devicePixelContentBoxSize && devicePixelContentBoxSize[0]) {
                    width = devicePixelContentBoxSize[0].inlineSize
                    height = devicePixelContentBoxSize[0].blockSize
                } else {
                    // Fallback to `contentRect` for environments that don't support `devicePixelContentBoxSize`
                    // eslint-disable-next-line prefer-destructuring
                    width = contentRect.width
                    // eslint-disable-next-line prefer-destructuring
                    height = contentRect.height
                }

                const area = width * height

                if (area < prevArea) {
                    setDidResize(true)
                }

                prevArea = area
            }
        })

        if (messagesContainerRef?.current) {
            resizeObserver.current.observe(messagesContainerRef.current)
        }

        if (!selectedMessages || selectedMessages.length < historyLength) {
            setIsLoadingHistory(true)
            dispatch(loadHistory({
                ...chat,
                history_length: historyLength,
                ...historyCursorMessageId && {message_id: historyCursorMessageId},
            }))
                .unwrap()
                .then((action) => {
                    const history_length = Array.isArray(action)
                        ? (action[0] as NinchatHistoryResultsEvent).history_length // eslint-disable-line no-extra-parens
                        : (action as NinchatHistoryResultsEvent).history_length // eslint-disable-line no-extra-parens
                    setHasMoreHistory(history_length === historyLength)
                })
                // Cached messages remain usable while disconnected. Retrying history
                // for a channel opened offline is tracked separately.
                .catch(() => undefined)
                .finally(() => {
                    setIsLoadingHistory(false)
                })
        }

        setScrollToElement(BOTTOM)
        setForcePosition(true)

        return () => {
            resizeObserver.current?.disconnect()
        }
    }, [])

    useEffect(() => {
        if (previousSessionRevision.current === sessionRevision) {
            return
        }

        previousSessionRevision.current = sessionRevision
        const historyLength = 50

        setIsLoadingHistory(true)
        dispatch(loadHistory({
            ...chat,
            history_length: historyLength,
        }))
            .unwrap()
            .then((action) => {
                const history_length = Array.isArray(action)
                    ? (action[0] as NinchatHistoryResultsEvent).history_length // eslint-disable-line no-extra-parens
                    : (action as NinchatHistoryResultsEvent).history_length // eslint-disable-line no-extra-parens
                setHasMoreHistory(history_length === historyLength)
            })
            // A connection can drop again before the reconnect history request runs.
            .catch(() => undefined)
            .finally(() => {
                setIsLoadingHistory(false)
            })

        setScrollToElement(BOTTOM)
        setForcePosition(true)
    }, [sessionRevision])

    useEffect(() => {
        if (!isReady) {
            return
        }

        setWritingMessages((prev) => {
            if (!writingMemberUserAttrs?.length && !prev?.length) {
                return prev
            }

            const current = (writingMemberUserAttrs ?? []).map((userAttrs) => ({
                _icon: userAttrs.iconurl,
                message_id: userAttrs.user_id,
                message_type: NinchatMessageTypes.NinchatWritingIndicatorMessage,
                message_user_name: userAttrs.name,
            } as NinchatStoreMessage))

            const next: NinchatStoreMessage[] = []

            next.push(...(prev ?? []).filter((a) => current.some((b) => a.message_id === b.message_id)))
            next.push(...current.filter((a) => next.every((b) => a.message_id !== b.message_id)))

            return next
        })
    }, [writingMemberUserAttrs, isReady])

    useEffect(() => {
        if (!selectedMessages && !writingMessages && !messages && !isEmbedAudience) {
            return
        }

        if (isEmbedQueueChannel && isChatActive) {
            const receivedMessagesLength = selectedMessages?.filter((m) => m.message_user_id !== userId).length
            if (receivedMessagesLength > receivedMessages) {
                setReceivedMessages(receivedMessagesLength)


                sendToParent({
                    event: EmbedEvent.EmbedSound,
                    sound: 'messageReceived',
                    targetOrigin,
                })

                sendToParent({
                    channel_id: chat.channel_id || '',
                    event: EmbedEvent.MessageReceived,
                    targetOrigin,
                })
            }
        }

        const initialMessages: any = []
        if (isEmbedAudience) {
            initialMessages.push({
                customText: embed?.audienceStartedText ?
                    t(embed?.audienceStartedText || '', {queueName: currentQueue?.queue_attrs?.name || ''})
                    : t('Audience in queue {{queueName}} accepted.', {queueName: currentQueue?.queue_attrs?.name || ''}),
                message_type: NinchatMessages.NinchatInfoMessageTypes.NinchatInfoChannelMessage,
                renderAsMarkdown: true,
                skipTime: true,
            })
        }

        const conversationMessages = [...selectedMessages ?? []].sort((left, right) => {
            const timeDifference = (left.message_time ?? 0) - (right.message_time ?? 0)

            return timeDifference || left.message_id.localeCompare(right.message_id)
        })
        const currentMessages = [
            ...initialMessages,
            ...conversationMessages,
            ...writingMessages ?? [],
        ].map((item) => {
            const isJoinOrPart = item.message_type === 'ninchat.com/info/join' || item.message_type === 'ninchat.com/info/part'
            const isHiddenJoinPart = isChannel && !isAudienceChannel && hideJoinAndPartMessages && isJoinOrPart
            let isHidden = !!(item.message_hidden || isHiddenJoinPart)

            if (isEmbedAudience && item.message_type === 'ninchat.com/info/join') {
                isHidden = true
            }

            return isHidden === item.message_hidden
                ? item
                : {
                    ...item,
                    message_hidden: isHidden,
                }
        })

        setMessages(currentMessages)
    }, [selectedMessages, writingMessages, currentQueue])

    useEffect(() => {
        if (!isInitialized.current) {
            return
        }

        setScrollToBottomView?.({
            display: bottomElement.isVisible ? 'hide' : 'show',
            isHighlighting: chatStatus === 'highlight',
            onClick: () => {
                setScrollToElement(BOTTOM)
                setForcePosition(true)
            },
            type: isChatActive ? 'newMessage' : 'simple',
        })
    }, [messages])

    useEffect(() => {
        if (!isInitialized.current) {
            return
        }

        if (scrollToElement) {
            setForcePosition(true)
        }
    }, [topElement.ref])

    useEffect(() => {
        if (!isInitialized.current) {
            return
        }

        if (scrollToElement === BOTTOM) {
            setForcePosition(true)
        }
    }, [bottomElement.ref])

    useEffect(() => {
        if (!isInitialized.current) {
            return
        }

        if (topElement.isVisible && hasMoreHistory !== false) {
            const historyLength = 20

            setIsLoadingHistory(true)
            dispatch(loadHistory({
                ...chat,
                history_length: historyLength,
                ...historyCursorMessageId && {message_id: historyCursorMessageId},
            }))
                .unwrap()
                .then((action) => {
                    const history_length = Array.isArray(action)
                        ? (action[0] as NinchatHistoryResultsEvent).history_length // eslint-disable-line no-extra-parens
                        : (action as NinchatHistoryResultsEvent).history_length // eslint-disable-line no-extra-parens
                    setHasMoreHistory(history_length === historyLength)
                })
                .finally(() => {
                    setIsLoadingHistory(false)
                })

            if (topElement.ref) {
                middleElement.setRef(topElement.ref)
                setScrollToElement(MIDDLE)
            }
        }
    }, [topElement.isVisible])

    useEffect(() => {
        if (!isInitialized.current) {
            return
        }

        if (bottomElement.isVisible) {
            setScrollToElement(BOTTOM)
            setForcePosition(false)
            setDidResize(false)
        } else if (!bottomElement.isVisible && !didResize) {
            setScrollToElement(undefined)
            setForcePosition(false)
        }

        setScrollToBottomView?.({
            display: bottomElement.isVisible ? 'hide' : 'show',
            isHighlighting: false,
            onClick: () => {
                setScrollToElement(BOTTOM)
                setForcePosition(true)
            },
            type: 'simple',
        })
    }, [bottomElement.isVisible])

    useEffect(() => {
        if (!isInitialized.current) {
            return
        }

        if (scrollToElement) {
            const element = scrollToElement === TOP ? topElement : scrollToElement === MIDDLE ? middleElement : scrollToElement === BOTTOM ? bottomElement : undefined

            const middleAndTopAreVisible = scrollToElement === MIDDLE && topElement.isVisible && middleElement.isVisible

            if (element && element.ref && (!element.isVisible || middleAndTopAreVisible || forcePosition) && (forcePosition || didResize)) {
                if (isEmbedQueueChannel && isEmbedContainer) {
                    const container = messagesContainerRef?.current

                    container?.scrollTo({
                        behavior: 'smooth',
                        top: container.scrollHeight - container.clientHeight,
                    })
                } else {
                    element.ref.scrollIntoView()
                }

                setForcePosition(false)
                setDidResize(false)

                if (scrollToElement === MIDDLE) {
                    setScrollToElement(undefined)
                    element.setRef(undefined)
                }
            }
        }
    })

    useEffect(() => {
        const container = messagesContainerRef?.current

        const onScroll = () => {
            if (!container) {
                return
            }

            const {
                scrollTop, scrollHeight, clientHeight,
            } = container

            if (scrollHeight - clientHeight - scrollTop <= 1) {
                setScrollAtTheBottom(true)
            } else {
                setScrollAtTheBottom(false)
            }
        }

        container?.addEventListener('scroll', onScroll)

        return () => {
            container?.removeEventListener('scroll', onScroll)
        }
    }, [messagesContainerRef])

    useEffect(() => {
        setHistoryLoadStatus?.(isLoadingHistory)
    }, [isLoadingHistory])

    useEffect(() => {
        if (!isInitialized.current && topElement.ref && !bottomElement.ref && selectedMessages?.length === 1 && hasMoreHistory === false) {
            bottomElement.setRef(topElement.ref)
        }
    }, [topElement.ref, hasMoreHistory])

    useEffect(() => {
        if (isChatActive && (bottomElement.isVisible || scrollAtTheBottom) && !idle && selectedMessages) {
            for (const message of [...selectedMessages].reverse()) {
                if (message.message_user_id !== userId) {
                    dispatch(updateSession({
                        ...chat,
                        message_id: message.message_id,
                    }))

                    break
                }
            }
        }
    }, [chatStatus, bottomElement.isVisible, idle, scrollAtTheBottom])

    return (
        <MessagesView
            bottomElementSetRef={bottomElement.setRef}
            channelId={chat.channel_id || ''}
            currentQueue={currentQueue || null}
            isAudienceChannel={isAudienceChannel}
            isChannel={isChannel}
            isNarrow={isNarrow}
            messages={messages}
            onAction={onAction}
            onDeleteMessage={onDeleteMessage}
            onRemovePendingMessage={onRemovePendingMessage}
            onRetryMessage={onRetryMessage}
            topElementSetRef={topElement.setRef}
        />
    )
}

export default Messages
